import type { Map as MlMap, StyleSpecification } from 'maplibre-gl';

/**
 * Tile prefetcher — quietly fetches tiles around the visible viewport so
 * panning to neighbouring areas is instant. All fetches go through the
 * service worker, which caches them permanently.
 *
 * The default prefetch radius is 2 tiles in each direction (5×5 = 25 tiles).
 * MapLibre's idle event fires after pan/zoom stops, so we throttle and skip
 * if we already prefetched this viewport.
 */

const lastPrefetchKey = new WeakMap<MlMap, string>();
const inflight = new Set<string>();

/** Prefetch tiles around the current viewport (background, fire-and-forget). */
export function prefetchAroundViewport(map: MlMap, radius = 2): void {
  if (!map.isStyleLoaded()) return;
  const z = Math.floor(map.getZoom());
  if (z < 0 || z > 22) return;
  const center = map.getCenter();
  const cx = lonToTileX(center.lng, z);
  const cy = latToTileY(center.lat, z);
  // Skip if same key as last time.
  const key = `${z}:${cx}:${cy}:${radius}`;
  if (lastPrefetchKey.get(map) === key) return;
  lastPrefetchKey.set(map, key);

  const templates = collectTileTemplates(map.getStyle());
  if (templates.length === 0) return;

  // Build a list of (x, y) tile coords in a radius around the viewport, skip
  // the center 3×3 (those are already visible / loading).
  const n = 1 << z;
  const tiles: Array<[number, number]> = [];
  for (let dx = -radius; dx <= radius; dx++) {
    for (let dy = -radius; dy <= radius; dy++) {
      if (Math.abs(dx) <= 1 && Math.abs(dy) <= 1) continue;
      const x = cx + dx;
      const y = cy + dy;
      if (x < 0 || y < 0 || x >= n || y >= n) continue;
      tiles.push([x, y]);
    }
  }

  // Stagger fetches so we don't burst the network.
  let i = 0;
  for (const template of templates) {
    for (const [x, y] of tiles) {
      const url = template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
      if (inflight.has(url)) continue;
      inflight.add(url);
      window.setTimeout(() => {
        fetch(url, { credentials: 'omit', mode: 'cors' })
          .catch(() => {
            /* swallow — prefetch is best-effort */
          })
          .finally(() => inflight.delete(url));
      }, i * 25);
      i++;
    }
  }
}

/** Pre-cache a wider area at multiple zoom levels — invoked from Settings. */
export async function prefetchCurrentView(
  map: MlMap,
  opts: { zoomDelta?: number; radius?: number; onProgress?: (done: number, total: number) => void } = {},
): Promise<{ requested: number; cached: number }> {
  const zoomDelta = opts.zoomDelta ?? 2;
  const radius = opts.radius ?? 5;
  const onProgress = opts.onProgress;

  if (!map.isStyleLoaded()) return { requested: 0, cached: 0 };
  const templates = collectTileTemplates(map.getStyle());
  if (templates.length === 0) return { requested: 0, cached: 0 };

  const baseZ = Math.floor(map.getZoom());
  const center = map.getCenter();
  const urls: string[] = [];

  for (let zoff = 0; zoff <= zoomDelta; zoff++) {
    const z = baseZ + zoff;
    if (z < 0 || z > 22) continue;
    const cx = lonToTileX(center.lng, z);
    const cy = latToTileY(center.lat, z);
    const n = 1 << z;
    const r = radius + zoff * 2;
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= n || y >= n) continue;
        for (const template of templates) {
          urls.push(template.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y)));
        }
      }
    }
  }

  let done = 0;
  let cached = 0;
  // Run with bounded concurrency to avoid the browser refusing new sockets.
  const CONCURRENCY = 8;
  let cursor = 0;
  async function worker() {
    while (cursor < urls.length) {
      const url = urls[cursor++]!;
      try {
        const r = await fetch(url, { credentials: 'omit', mode: 'cors' });
        if (r.ok) cached++;
      } catch {
        /* swallow */
      }
      done++;
      if (onProgress) onProgress(done, urls.length);
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  return { requested: urls.length, cached };
}

/** Collect tile URL templates from a style. Handles `tiles[]` directly and the
 *  TileJSON `url` form (which we resolve via fetch, cached by browser). */
function collectTileTemplates(style: StyleSpecification): string[] {
  const out: string[] = [];
  if (!style?.sources) return out;
  for (const source of Object.values(style.sources)) {
    if (!source || typeof source !== 'object') continue;
    const s = source as { type?: string; tiles?: string[]; url?: string };
    if (s.type !== 'vector' && s.type !== 'raster' && s.type !== 'raster-dem') continue;
    if (Array.isArray(s.tiles) && s.tiles.length > 0) {
      const first = s.tiles[0];
      if (first) out.push(first);
    }
    // Sources with `url` (TileJSON) are skipped — would need an extra fetch + parse.
    // OpenFreeMap, MapTiler, Mapbox styles all eventually expose `tiles[]` once
    // they're loaded. If we miss a TileJSON-only source, we just don't prefetch
    // it; the visible tiles are still fetched normally.
  }
  return out;
}

function lonToTileX(lon: number, z: number): number {
  return Math.floor(((lon + 180) / 360) * (1 << z));
}

function latToTileY(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * (1 << z),
  );
}
